import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  styled,
  CircularProgress,
} from "@mui/material";
import { verifyOTP, sendOTP } from "@services/auth";
import { useNavigate, useLocation } from "react-router-dom";
import ContentUsBgImage from "@assets/ContentUsBgImage.png";
const SignUpContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  padding: "16px",
  [theme.breakpoints.down("sm")]: {
    padding: "8px",
    alignItems: "flex-start",
    paddingTop: "20px",
  },
}));

const ContactSection = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px 0px",
  position: "relative",
  width: "100%",
  maxWidth: "900px",
  margin: "0 auto",
  gap: 16,
  [theme.breakpoints.down("lg")]: {
    gap: 12,
    flexDirection: "column",
    padding: "12px",
    maxWidth: "900px",
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    padding: "16px 8px",
    gap: 10,
    maxWidth: "100%",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "12px 6px",
    gap: 8,
  },
}));

const BackgroundOverlay = styled(Box)({
  position: "absolute",
  height: "100%",
  left: 0,
  top: 0,
  width: "100%",
  backgroundImage: `
    radial-gradient(ellipse 42.02px 68.593px at 298.8px 124.35px, rgba(220,253,255,0.4) 0%, rgba(255,255,255,0) 100%),
    radial-gradient(ellipse 77.28px 6.0674px at 1471.2px 363.44px, rgba(220,253,255,0.4) 0%, rgba(255,255,255,0) 100%),
    radial-gradient(ellipse 1.62px 57.671px at 682.2px 879.48px, rgba(197,255,225,0.4) 0%, rgba(255,255,255,0) 100%)
  `,
});
const LeftPanel = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "620px",
  height: "520px",
  backgroundImage: `url(${ContentUsBgImage})`,
  borderRadius: "32px",
  border: "1px solid #ffffff",
  overflow: "hidden",
  marginRight: "-80px",
  flexShrink: 0,
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
  [theme.breakpoints.down("lg")]: {
    width: "480px",
    height: "400px",
    marginRight: "-20px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "500px",
    height: "auto",
    minHeight: "240px",
    aspectRatio: "16 / 10",
    marginRight: 0,
    borderRadius: "24px",
  },
  [theme.breakpoints.down("sm")]: {
    maxWidth: "100%",
    minHeight: "240px",
    borderRadius: "16px",
  },
}));

const LeftPanelContent = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: "95.5px",
  top: "101px",
  width: "470px",
  color: "#ffffff",
  [theme.breakpoints.down("lg")]: {
    left: "32px",
    top: "48px",
    width: "380px",
  },
  [theme.breakpoints.down("md")]: {
    position: "relative",
    left: 0,
    top: 0,
    width: "100%",
    padding: "16px 12px 12px",
    textAlign: "center",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "16px 12px 12px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "64px",
  lineHeight: "1.1",
  marginBottom: "40px",
  color: "#ffffff",
  [theme.breakpoints.down("lg")]: {
    fontSize: "42px",
    marginBottom: "24px",
  },
  [theme.breakpoints.down("md")]: {
    fontSize: "36px",
    marginBottom: "12px",
    lineHeight: "1.2",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "28px",
    marginBottom: "12px",
    paddingTop: "48px",
    textAlign: "center",
  },
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "18px",
  lineHeight: "1.5",
  color: "#ffffff",
  width: "422px",
  textAlign: "center",
  whiteSpace: "normal",
  overflowWrap: "normal",
  wordBreak: "normal",
  hyphens: "none",
  [theme.breakpoints.down("lg")]: {
    width: "360px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    fontSize: "16px",
    lineHeight: "1.45",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
    lineHeight: "1.4",
    maxWidth: 300,
    margin: "0 auto",
    textAlign: "center",
    whiteSpace: "normal",
    overflowWrap: "normal",
    wordBreak: "normal",
    hyphens: "none",
  },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  backgroundColor: "#f4feff",
  border: "1px solid #05495a",
  borderRadius: "16px",
  padding: "32px 28px 32px 32px",
  width: "480px",
  position: "relative",
  flexShrink: 0,
  minHeight: 500,
  [theme.breakpoints.down("lg")]: {
    width: "420px",
    padding: "28px 24px 28px 28px",
    minHeight: 540,
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "480px",
    padding: "24px 18px 24px 24px",
    minHeight: 520,
  },
  [theme.breakpoints.down("sm")]: {
    padding: "20px 14px 20px 20px",
    borderRadius: "12px",
    margin: "0 6px",
    maxWidth: "90%",
    minHeight: 500,
  },
}));

const FormContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "32px",
  width: "100%",
  height: "100%",
});

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "32px",
  alignItems: "flex-start",
  width: "100%",
  [theme.breakpoints.down("sm")]: {
    gap: "10px",
  },
}));

const BackButton = styled(Button)({
  minWidth: "32px",
  width: "32px",
  height: "32px",
  padding: 0,
  color: "#033843",
  "&:hover": {
    backgroundColor: "rgba(3, 56, 67, 0.04)",
  },
});

const TitleContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "8px",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 0px",
  flex: 1,
  [theme.breakpoints.down("md")]: {
    padding: 0,
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 700,
  fontSize: "28px",
  lineHeight: "35px",
  color: "#033843",
  height: "35px",
  // textAlign: "center",
  flex: 1,
  whiteSpace: "nowrap",
  [theme.breakpoints.down("sm")]: {
    fontSize: "24px",
    lineHeight: "30px",
  },
}));

const PageSubtitle = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "20px",
  lineHeight: "1.5",
  color: "#262626",
  textAlign: "center",
  width: "100%",
});

const OTPContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "40px",
  alignItems: "center",
  padding: "24px 0",
  flex: 1,
  width: "100%",
});

const OTPInputContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "31px",
  alignItems: "center",
  justifyContent: "center",
  [theme.breakpoints.down("sm")]: {
    gap: "12px",
  },
}));

const OTPInput = styled(TextField)(({ theme }) => ({
  width: "52px",
  height: "52px",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    borderRadius: "4px",
    width: "52px",
    height: "52px",
    "& fieldset": {
      borderColor: "#e6fafc",
    },
    "&:hover fieldset": {
      borderColor: "#bfe5ec",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0a829d",
    },
    boxShadow:
      "inset 0px 0px 8px 0px rgba(0,108,116,0.12), inset 0px 0px 4px 0px rgba(34,110,127,0.25)",
  },
  "& .MuiOutlinedInput-input": {
    padding: 0,
    fontSize: "18px",
    fontFamily: "Rethink Sans, sans-serif",
    textAlign: "center",
    fontWeight: 600,
  },
  [theme.breakpoints.down("sm")]: {
    width: "44px",
    height: "44px",
    "& .MuiOutlinedInput-root": {
      width: "44px",
      height: "44px",
    },
    "& .MuiOutlinedInput-input": {
      fontSize: "16px",
    },
  },
}));

const ResendContainer = styled(Box)({
  textAlign: "center",
});

const ResendText = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "18px",
  lineHeight: "normal",
  color: "#374151",
  "& .countdown": {
    fontWeight: 700,
    color: "#374151",
  },
});

const ResendLink = styled("span")({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "18px",
  fontWeight: 700,
  color: "#0a829d",
  cursor: "pointer",
  textDecoration: "underline",
  "&:hover": {
    color: "#086e86",
  },
});

const ContinueButton = styled(Button)<{ isActive: boolean }>(
  ({ isActive }) => ({
    backgroundColor: isActive
      ? "linear-gradient(90deg, #0a829d 0%, #35d7e4 100%)"
      : "#ddf0f4",
    border: "1px solid #ffffff",
    borderRadius: "60px",
    height: "60px",
    width: "100%",
    padding: "12px 24px",
    textTransform: "none",
    fontFamily: "Rethink Sans, sans-serif",
    fontWeight: 600,
    fontSize: "18px",
    lineHeight: "22px",
    color: isActive ? "#ffffff" : "#bad6dc",
    background: isActive
      ? "linear-gradient(90deg, #0a829d 0%, #35d7e4 100%)"
      : "#ddf0f4",
    boxShadow: isActive
      ? "0px 4px 24px 0px rgba(10,130,157,0.2), 0px 4px 7.3px 0px rgba(133,233,255,0.4)"
      : "none",
    "&:hover": {
      background: isActive
        ? "linear-gradient(90deg, #086e86 0%, #2bc9d6 100%)"
        : "#ddf0f4",
    },
    "&:disabled": {
      background: "#ddf0f4",
      color: "#bad6dc",
    },
  })
);

interface LocationState {
  email?: string;
}

export default function VerifyOTP() {
  const [otp, setOtp] = useState<string[]>(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(59);
  const [canResend, setCanResend] = useState(false);
  const [resending, setResending] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  const email = state?.email;

  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  useEffect(() => {
    if (!email) {
      navigate("/forgot-password");
      return;
    }
  }, [email, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  const handleBack = () => {
    navigate("/forgot-password");
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) {
      value = value.slice(-1);
    }

    if (!/^\d*$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasteData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 4);
    const newOtp = ["", "", "", ""];

    for (let i = 0; i < pasteData.length; i++) {
      newOtp[i] = pasteData[i];
    }

    setOtp(newOtp);

    const lastIndex = Math.min(pasteData.length - 1, 3);
    inputRefs[lastIndex].current?.focus();
  };

  const handleResend = async () => {
    if (!email || !canResend || resending) return;

    setResending(true);
    setError(null);
    setSuccess(null);

    try {
      await sendOTP(email);
      setSuccess("OTP sent successfully!");
      setCountdown(59);
      setCanResend(false);
      setOtp(["", "", "", ""]);
      inputRefs[0].current?.focus();
    } catch (err: any) {
      let errorMessage = "Failed to resend OTP. Please try again.";
      try {
        const parsed = JSON.parse(err.message);
        errorMessage = parsed?.detail || parsed?.message || errorMessage;
      } catch {
        errorMessage = err.message || errorMessage;
      }
      setError(errorMessage);
    } finally {
      setResending(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || otp.some((digit) => !digit)) return;

    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const otpString = otp.join("");
      const response = await verifyOTP(email, otpString);

      if (response.success) {
        setSuccess("OTP verified successfully!");
        // Navigate to reset password page
        setTimeout(() => {
          navigate("/reset-password", { state: { email } });
        }, 1500);
      } else {
        setError(response.message || "OTP verification failed");
      }
    } catch (err: any) {
      let errorMessage = "OTP verification failed. Please try again.";
      try {
        const parsed = JSON.parse(err.message);
        errorMessage = parsed?.detail || parsed?.message || errorMessage;
      } catch {
        errorMessage = err.message || errorMessage;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const isButtonActive = otp.every((digit) => digit !== "");

  const formatCountdown = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  if (!email) {
    return null;
  }

  return (
    <SignUpContainer>
      <ContactSection>
        <BackgroundOverlay />

        <LeftPanel>
          <LeftPanelContent>
            <MainTitle>Welcome Back to UniComply</MainTitle>
            <SubTitle>
              Stay connected with clinics nationwide ensuring better healthcare
              compliance.
            </SubTitle>
          </LeftPanelContent>
        </LeftPanel>

        <RightPanel>
          <FormContainer>
            <HeaderContainer>
              <BackButton onClick={handleBack}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"
                    fill="currentColor"
                  />
                </svg>
              </BackButton>
              <TitleContainer>
                <PageTitle>Verify OTP</PageTitle>
              </TitleContainer>
            </HeaderContainer>

            <PageSubtitle>
              Enter the code sent to your registered email
            </PageSubtitle>

            <OTPContainer>
              <Box
                component="form"
                onSubmit={onSubmit}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "40px",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <OTPInputContainer>
                  {otp.map((digit, index) => (
                    <OTPInput
                      key={index}
                      inputRef={inputRefs[index]}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={index === 0 ? handlePaste : undefined}
                      inputProps={{
                        maxLength: 1,
                        autoComplete: "off",
                      }}
                      variant="outlined"
                    />
                  ))}
                </OTPInputContainer>

                <ResendContainer>
                  {canResend ? (
                    <ResendText>
                      Didn't receive the email?{" "}
                      <ResendLink
                        onClick={handleResend}
                        style={{ opacity: resending ? 0.6 : 1 }}
                      >
                        {resending ? "Resending..." : "Resend"}
                      </ResendLink>
                    </ResendText>
                  ) : (
                    <ResendText>
                      Didn't receive the email? Resend in{" "}
                      <span className="countdown">
                        {formatCountdown(countdown)}
                      </span>
                    </ResendText>
                  )}
                </ResendContainer>
              </Box>
            </OTPContainer>

            {error && (
              <Typography
                color="error"
                variant="body2"
                sx={{ mt: -2, textAlign: "center" }}
              >
                {error}
              </Typography>
            )}

            {success && (
              <Typography
                color="success.main"
                variant="body2"
                sx={{ mt: -2, textAlign: "center" }}
              >
                {success}
              </Typography>
            )}

            <ContinueButton
              type="submit"
              disabled={loading || !isButtonActive}
              isActive={isButtonActive}
              onClick={onSubmit}
            >
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <CircularProgress size={20} color="inherit" />
                  VERIFYING...
                </Box>
              ) : (
                "CONTINUE"
              )}
            </ContinueButton>
          </FormContainer>
        </RightPanel>
      </ContactSection>
    </SignUpContainer>
  );
}
